/**
 * Packages & Services builder — package create/edit form (shell:
 * name + description + price + billing + active). Extracted verbatim
 * from the former AgencyServicesPage monolith (Task 150-A). No behavior
 * change: same `upsertAgencyPackage` payload, same validation.
 */

import { useState, type FormEvent } from 'react';
import { upsertAgencyPackage } from '../../api/endpoints';
import type { AgencyPackageRow, PackageBillingInterval } from '../../api/types';
import { PACKAGE_BILLING_INTERVALS } from './constants';
import { FormPanel, SimpleField, SimpleSelect } from './formPrimitives';
import { handleApiErr } from './helpers';

export function PackageForm({
  mode,
  initial,
  onClose,
  onSaved,
}: {
  readonly mode: 'create' | 'edit';
  readonly initial: AgencyPackageRow | null;
  readonly onClose: () => void;
  readonly onSaved: () => void;
}) {
  const [name, setName] = useState(initial?.name ?? '');
  const [description, setDescription] = useState(initial?.description ?? '');
  const [priceAmount, setPriceAmount] = useState(
    initial?.price_amount !== null && initial?.price_amount !== undefined
      ? String(initial.price_amount)
      : '',
  );
  const [priceCurrency, setPriceCurrency] = useState(initial?.price_currency ?? 'SAR');
  const [billingInterval, setBillingInterval] = useState<PackageBillingInterval | ''>(
    initial?.billing_interval ?? '',
  );
  const [isActive, setIsActive] = useState(initial?.is_active ?? true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function onSubmit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    setError(null);
    const trimmed = name.trim();
    if (trimmed.length === 0) {
      setError('اسم الباقة مطلوب.');
      return;
    }
    let parsedAmount: number | null = null;
    if (priceAmount.trim().length > 0) {
      const n = Number(priceAmount);
      if (!Number.isFinite(n) || n < 0) {
        setError('السعر يجب أن يكون رقماً غير سالب.');
        return;
      }
      parsedAmount = n;
    }
    setSubmitting(true);
    try {
      await upsertAgencyPackage({
        input: {
          ...(initial?.id ? { id: initial.id } : {}),
          name: trimmed,
          description: description.trim() || null,
          price_amount: parsedAmount,
          price_currency: priceCurrency.toUpperCase(),
          billing_interval: billingInterval === '' ? null : billingInterval,
          is_active: isActive,
        },
      });
      onSaved();
    } catch (err) {
      setSubmitting(false);
      handleApiErr(err, setError);
    }
  }

  return (
    <FormPanel title={mode === 'create' ? 'باقة جديدة' : `تعديل: ${initial?.name ?? ''}`}>
      <form onSubmit={onSubmit} noValidate>
        {error ? (
          <div className="banner banner-error" style={{ marginBottom: 12 }}>
            {error}
          </div>
        ) : null}
        <SimpleField
          label="اسم الباقة"
          required
          value={name}
          onChange={setName}
          maxLength={160}
          placeholder="مثال: باقة النمو الكاملة"
        />
        <SimpleField
          label="الوصف (اختياري)"
          value={description}
          onChange={setDescription}
          textarea
          maxLength={2000}
        />
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit,minmax(220px,1fr))',
            gap: 14,
            marginTop: 12,
          }}
        >
          <SimpleField
            label="السعر (اختياري)"
            value={priceAmount}
            onChange={setPriceAmount}
            type="number"
            dirLtr
          />
          <SimpleField
            label="العملة"
            value={priceCurrency}
            onChange={setPriceCurrency}
            maxLength={3}
            dirLtr
          />
          <SimpleSelect
            label="فترة الفوترة"
            value={billingInterval}
            onChange={(v) => setBillingInterval(v as PackageBillingInterval | '')}
            options={PACKAGE_BILLING_INTERVALS.map((i) => ({ value: i.value, label: i.label }))}
          />
        </div>
        <p className="tiny" style={{ color: 'var(--muted)', marginTop: 12 }}>
          ملاحظة: الباقة لا تملك قدرة بنفسها — القدرات تأتي من الخدمات التي تضمّها. ستُضيف الخدمات
          من قسم "تركيب الباقة" بالأسفل بعد حفظ الباقة.
        </p>
        <label
          style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 12, fontSize: 13 }}
        >
          <input
            type="checkbox"
            checked={isActive}
            onChange={(e) => setIsActive(e.currentTarget.checked)}
            disabled={submitting}
          />
          <span>الباقة نشطة</span>
        </label>
        <div style={{ display: 'flex', gap: 10, marginTop: 14 }}>
          <button
            type="submit"
            className="auth-cta"
            disabled={submitting}
            style={{ width: 'auto' }}
          >
            {submitting ? <span className="spinner" /> : null}
            <span>{submitting ? 'جاري الحفظ…' : 'حفظ'}</span>
          </button>
          <button
            type="button"
            className="btn-text"
            onClick={onClose}
            disabled={submitting}
            style={{ padding: '10px 14px' }}
          >
            إلغاء
          </button>
        </div>
      </form>
    </FormPanel>
  );
}
